import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import API_BASE_URL from '../../config/api';
import { Package, CheckCircle, XCircle, Clock, Search, Filter, Eye, Calendar, RefreshCw, Receipt } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { useConfirm } from '../../context/ConfirmContext';
import AutoRefreshIndicator from '../../components/AutoRefreshIndicator';
import InvoiceView from '../../components/InvoiceView';

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

const EPinRequests = () => {
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const [requests, setRequests] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [processing, setProcessing] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [viewInvoiceReqId, setViewInvoiceReqId] = useState(null);

    // Auto-refresh state
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    const fetchRequests = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setIsRefreshing(true);

        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/epin_requests.php`);
            url.searchParams.append('_t', Date.now());
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);

            const response = await fetch(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            const data = await response.json();

            if (data.success && isMountedRef.current) {
                setRequests(data.data);
                setCounts(data.counts);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
            if (!silent) showAlert('Failed to fetch requests', 'error');
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [showAlert, fromDate, toDate]);

    // Initial fetch
    useEffect(() => {
        isMountedRef.current = true;
        fetchRequests(false);

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchRequests]);

    // Auto-refresh interval
    useEffect(() => {
        if (autoRefreshEnabled) {
            intervalRef.current = setInterval(() => {
                fetchRequests(true); // Silent refresh
            }, AUTO_REFRESH_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [autoRefreshEnabled, fetchRequests]);

    const toggleAutoRefresh = () => setAutoRefreshEnabled(prev => !prev);

    const handleAction = async (requestId, action) => {
        const actionText = action === 'approve' ? 'approve' : 'reject';
        const confirmed = await showConfirm(
            `${action === 'approve' ? 'Approve' : 'Reject'} E-Pin Request`,
            `Are you sure you want to ${actionText} this E-Pin request?`,
            action === 'approve' ? 'check' : 'x'
        );

        if (!confirmed) return;

        setProcessing(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/epin_action.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ requestId, action, remarks })
            });

            const responseText = await response.text();

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                console.error("JSON Parse Error:", parseError);
                console.error("Response Text:", responseText);
                showAlert('Server error: Invalid response format', 'error');
                return;
            }

            if (data.success) {
                showAlert(data.message, 'success');
                setShowModal(false);
                setRemarks('');
                fetchRequests();
            } else {
                showAlert(data.message || 'Action failed', 'error');

                // If request was already processed, refresh the list to show current state
                if (data.message && data.message.includes('already processed')) {
                    setShowModal(false);
                    fetchRequests();
                }
            }
        } catch (error) {
            console.error('Error processing request:', error);
            showAlert('Failed to process request: ' + error.message, 'error');
        } finally {
            setProcessing(false);
        }
    };

    const filteredRequests = requests.filter(req => {
        const matchesFilter = filter === 'all' || req.status.toLowerCase() === filter;

        const userName = req.user_name || '';
        const userCode = req.user_code || '';
        const txnId = req.transaction_id || '';

        const matchesSearch = userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            txnId.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    const getStatusBadge = (status) => {
        const styles = {
            'Pending': 'bg-yellow-100 text-yellow-700',
            'Approved': 'bg-green-100 text-green-700',
            'Rejected': 'bg-red-100 text-red-700'
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${styles[status]}`}>
                {status}
            </span>
        );
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">E-Pin Requests</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage user E-Pin purchase requests</p>
                    </div>
                    <AutoRefreshIndicator
                        lastUpdated={lastUpdated}
                        isAutoRefreshEnabled={autoRefreshEnabled}
                        onToggle={toggleAutoRefresh}
                        onRefresh={() => fetchRequests(false)}
                        loading={isRefreshing}
                        intervalSeconds={30}
                    />
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { label: 'Total Requests', value: counts.total, icon: Package, color: 'indigo' },
                        { label: 'Pending', value: counts.pending, icon: Clock, color: 'yellow' },
                        { label: 'Approved', value: counts.approved, icon: CheckCircle, color: 'green' },
                        { label: 'Rejected', value: counts.rejected, icon: XCircle, color: 'red' }
                    ].map((stat, idx) => (
                        <div key={idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                                    <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                                </div>
                                <div className={`p-3 bg-${stat.color}-50 text-${stat.color}-600 rounded-xl`}>
                                    <stat.icon size={24} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by user name, code, or transaction ID..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                                />
                            </div>
                            <span className="text-gray-300 text-xs font-bold">to</span>
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                                <Calendar size={16} className="text-gray-400" />
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-transparent text-xs font-bold focus:outline-none cursor-pointer"
                                />
                            </div>

                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <select
                                    className="pl-10 pr-8 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-bold text-xs"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>

                            <button
                                onClick={() => fetchRequests()}
                                className="px-4 py-3 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center gap-2"
                            >
                                <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                                Filter
                            </button>
                        </div>
                    </div>
                </div>

                {/* Requests Table / Mobile Cards */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-400">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                            <p className="font-medium animate-pulse">Loading growth partner requests...</p>
                        </div>
                    ) : filteredRequests.length === 0 ? (
                        <div className="p-12 text-center">
                            <Package className="mx-auto text-gray-100 mb-2" size={48} />
                            <p className="text-gray-400 font-medium italic">No requests found matching your filters.</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50/50 border-b border-gray-100 text-gray-500 text-[10px] font-black uppercase tracking-[0.1em]">
                                            <th className="px-6 py-4">Growth Partner</th>
                                            <th className="px-6 py-4">Package/Qty</th>
                                            <th className="px-6 py-4">Amount</th>
                                            <th className="px-6 py-4">Payment Detail</th>
                                            <th className="px-6 py-4">Request Date</th>
                                            <th className="px-6 py-4">Lifecycle</th>
                                            <th className="px-6 py-4 text-right">Operations</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredRequests.map((req) => (
                                            <tr key={req.id} className="hover:bg-indigo-50/20 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{req.user_name}</p>
                                                        <p className="text-[10px] font-bold text-gray-400 font-mono tracking-tighter">ID: {req.user_code}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="text-xs font-black text-gray-700">{req.package}</p>
                                                        <p className="text-[10px] font-bold text-indigo-500 uppercase">Unit Qty: {req.quantity}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-sm font-black text-gray-900">₹{parseFloat(req.amount).toLocaleString()}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="text-[10px] font-black text-gray-800 uppercase tracking-widest leading-none mb-1">{req.payment_mode}</p>
                                                        <p className="text-[9px] text-gray-400 font-mono italic truncate max-w-[120px]">{req.transaction_id}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-xs font-bold text-gray-500 italic">
                                                        {new Date(req.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {getStatusBadge(req.status)}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedRequest(req);
                                                                setShowModal(true);
                                                            }}
                                                            className="p-2.5 bg-white text-gray-400 hover:text-indigo-600 rounded-xl shadow-sm border border-gray-100 hover:border-indigo-100 transition-all active:scale-95"
                                                            title="View Details"
                                                        >
                                                            <Eye size={18} />
                                                        </button>
                                                        {req.status === 'Pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedRequest(req);
                                                                        handleAction(req.id, 'approve');
                                                                    }}
                                                                    className="p-2.5 bg-white text-green-500 hover:bg-green-50 rounded-xl shadow-sm border border-gray-100 hover:border-green-100 transition-all active:scale-95"
                                                                    title="Approve"
                                                                    disabled={processing}
                                                                >
                                                                    <CheckCircle size={18} />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedRequest(req);
                                                                        setShowModal(true);
                                                                    }}
                                                                    className="p-2.5 bg-white text-red-500 hover:bg-red-50 rounded-xl shadow-sm border border-gray-100 hover:border-red-100 transition-all active:scale-95"
                                                                    title="Reject"
                                                                    disabled={processing}
                                                                >
                                                                    <XCircle size={18} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {req.status === 'Approved' && (
                                                            <button
                                                                onClick={() => setViewInvoiceReqId(req.id)}
                                                                className="p-2.5 bg-white text-red-500 hover:bg-red-50 rounded-xl shadow-sm border border-gray-100 hover:border-red-100 transition-all active:scale-95"
                                                                title="View Invoice"
                                                            >
                                                                <Receipt size={18} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="lg:hidden divide-y divide-gray-50">
                                {filteredRequests.map((req) => (
                                    <div key={req.id} className="p-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-indigo-100">
                                                    {req.user_name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-black text-gray-900 leading-none mb-1">{req.user_name}</p>
                                                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest italic">{req.user_code}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {getStatusBadge(req.status)}
                                            </div>
                                        </div>

                                        <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 space-y-3">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-0.5">Package Details</span>
                                                    <p className="text-xs font-black text-gray-700">{req.package} <span className="text-indigo-600">x{req.quantity}</span></p>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-0.5">Total Amount</span>
                                                    <p className="text-sm font-black text-gray-900">₹{parseFloat(req.amount).toLocaleString()}</p>
                                                </div>
                                            </div>

                                            <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                                                <div>
                                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.1em] block mb-0.5">Payment Reference</span>
                                                    <p className="text-[10px] font-bold text-gray-600 uppercase italic truncate max-w-[150px]">{req.payment_mode} • {req.transaction_id}</p>
                                                </div>
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{new Date(req.created_at).toLocaleDateString()}</p>
                                            </div>

                                            <div className="pt-3 flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedRequest(req);
                                                        setShowModal(true);
                                                    }}
                                                    className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-700 text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm active:scale-95 transition-all"
                                                >
                                                    Details
                                                </button>
                                                {req.status === 'Pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedRequest(req);
                                                                handleAction(req.id, 'approve');
                                                            }}
                                                            className="flex-1 py-2.5 bg-green-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-green-100 active:scale-95 transition-all"
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedRequest(req);
                                                                setShowModal(true);
                                                            }}
                                                            className="px-3 py-2.5 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-red-100 transition-all active:scale-95"
                                                        >
                                                            <XCircle size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                {req.status === 'Approved' && (
                                                    <button
                                                        onClick={() => setViewInvoiceReqId(req.id)}
                                                        className="flex-1 py-2.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-red-100 active:scale-95 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Receipt size={14} /> Invoice
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Details Modal */}
            {showModal && selectedRequest && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <XCircle size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">User Name</p>
                                    <p className="font-bold text-gray-900">{selectedRequest.user_name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">User Code</p>
                                    <p className="font-bold text-gray-900">{selectedRequest.user_code}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Package</p>
                                    <p className="font-bold text-gray-900">{selectedRequest.package}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Quantity</p>
                                    <p className="font-bold text-gray-900">{selectedRequest.quantity}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Amount</p>
                                    <p className="font-bold text-gray-900">₹{parseFloat(selectedRequest.amount).toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Payment Mode</p>
                                    <p className="font-bold text-gray-900">{selectedRequest.payment_mode}</p>
                                </div>
                                <div className="col-span-1">
                                    <p className="text-sm text-gray-500">Transaction ID</p>
                                    <p className="font-mono text-sm font-bold text-gray-900">{selectedRequest.transaction_id}</p>
                                </div>
                                <div className="col-span-1">
                                    <p className="text-sm text-gray-500">User Transaction Date</p>
                                    <p className="font-bold text-gray-900">
                                        {selectedRequest.transaction_date ? new Date(selectedRequest.transaction_date).toLocaleString() : 'N/A'}
                                    </p>
                                </div>

                                {selectedRequest.payment_mode === 'Bank Transfer' && (
                                    <div className="col-span-2 p-4 bg-blue-50 rounded-xl border border-blue-100 grid grid-cols-2 gap-4 mt-2">
                                        <div className="col-span-2">
                                            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Sender Bank Details</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Sender A/C Holder</p>
                                            <p className="font-bold text-gray-900">{selectedRequest.sender_name || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Account Number</p>
                                            <p className="font-bold text-gray-900">{selectedRequest.sender_account_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">IFSC Code</p>
                                            <p className="font-bold text-gray-900">{selectedRequest.sender_ifsc || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Branch Name</p>
                                            <p className="font-bold text-gray-900">{selectedRequest.sender_branch || 'N/A'}</p>
                                        </div>
                                    </div>
                                )}

                                {selectedRequest.admin_remarks && (
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-500">Admin Remarks</p>
                                        <p className="text-gray-900">{selectedRequest.admin_remarks}</p>
                                    </div>
                                )}
                            </div>

                            {selectedRequest.screenshot && (
                                <div className="mt-4">
                                    <p className="text-xs text-gray-500 mb-2">Payment Proof</p>
                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-2">
                                        <img
                                            src={`${API_BASE_URL.replace('/api', '')}/${selectedRequest.screenshot}`}
                                            alt="Payment Proof"
                                            className="w-full h-auto max-h-96 object-contain rounded cursor-pointer hover:opacity-95 transition-opacity"
                                            onClick={() => window.open(`${API_BASE_URL.replace('/api', '')}/${selectedRequest.screenshot}`, '_blank')}
                                        />
                                        <p className="text-center text-xs text-gray-400 mt-2">Click image to view full size</p>
                                    </div>
                                </div>
                            )}

                            {selectedRequest.status === 'Pending' && (
                                <div className="mt-6 space-y-3">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks (Optional)</label>
                                    <textarea
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                        rows="3"
                                        placeholder="Add remarks..."
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t border-gray-200 flex gap-3">
                            {selectedRequest.status === 'Pending' ? (
                                <>
                                    <button
                                        onClick={() => handleAction(selectedRequest.id, 'approve')}
                                        disabled={processing}
                                        className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                                    >
                                        {processing ? 'Processing...' : 'Approve & Generate E-Pins'}
                                    </button>
                                    <button
                                        onClick={() => handleAction(selectedRequest.id, 'reject')}
                                        disabled={processing}
                                        className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
                                    >
                                        Reject Request
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-xl transition-colors"
                                >
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice View Modal */}
            {viewInvoiceReqId && (
                <InvoiceView
                    referenceId={viewInvoiceReqId}
                    invoiceType="epin"
                    onClose={() => setViewInvoiceReqId(null)}
                    isAdmin={true}
                />
            )}
        </Layout>
    );
};

export default EPinRequests;


